'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { OrderItem, Product } from '@/lib/supabaseService';
import { createOrder } from '@/lib/supabaseService';

interface PreOrderFormProps {
  items: OrderItem[];
  products: Product[];
  onOrderConfirmed: (refNumber: string, villaNumber: string) => void;
}

type FormValues = {
  villaNumber: string;
  phoneNumber: string;
};

export default function PreOrderForm({ items, onOrderConfirmed }: PreOrderFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = async (data: FormValues) => {
    if (items.length === 0) {
      setValidationError('Please select at least one item before placing your order.');
      return;
    }
    setValidationError('');
    setIsSubmitting(true);

    const { order, error } = await createOrder({
      villaNumber: data.villaNumber,
      phoneNumber: data.phoneNumber,
      items,
    });

    setIsSubmitting(false);

    if (error || !order) {
      setValidationError(error ?? 'Something went wrong placing your order. Please try again.');
      return;
    }

    onOrderConfirmed(order.referenceNumber, data.villaNumber);
  };

  return (
    <section id="order-form" className="px-4 py-6 max-w-sm mx-auto sm:max-w-md">
      <div className="divider-ornament mb-6 mx-0">
        <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'var(--secondary)' }}>
          Place Your Pre-Order
        </span>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <div>
          <label htmlFor="villaNumber" className="block text-sm font-semibold mb-1" style={{ color: 'var(--foreground)' }}>
            Villa / Flat Number
          </label>
          <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
            e.g. B-42 or Tower 3, Flat 201
          </p>
          <input
            id="villaNumber"
            type="text"
            placeholder="Your villa or flat number"
            className="input-field"
            {...register('villaNumber', { required: 'Villa number is required' })}
          />
          {errors.villaNumber && (
            <p className="text-xs mt-1" style={{ color: '#B91C1C' }}>
              {errors.villaNumber.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="phoneNumber" className="block text-sm font-semibold mb-1" style={{ color: 'var(--foreground)' }}>
            WhatsApp / Phone Number
          </label>
          <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
            We&apos;ll confirm your order on this number
          </p>
          <input
            id="phoneNumber"
            type="tel"
            placeholder="10-digit mobile number"
            className="input-field"
            {...register('phoneNumber', {
              required: 'Phone number is required',
              pattern: {
                value: /^[6-9]\d{9}$/,
                message: 'Enter a valid 10-digit Indian mobile number',
              },
            })}
          />
          {errors.phoneNumber && (
            <p className="text-xs mt-1" style={{ color: '#B91C1C' }}>
              {errors.phoneNumber.message}
            </p>
          )}
        </div>

        {validationError && (
          <p
            className="text-xs px-3 py-2 rounded-lg"
            style={{ color: '#B91C1C', backgroundColor: '#FEE2E2', border: '1px solid #FECACA' }}
          >
            {validationError}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full text-base py-4 mt-2"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Placing order…
            </span>
          ) : (
            'Confirm Pre-Order'
          )}
        </button>
        <p className="text-center text-xs text-gray-400 mt-2">No payment now — we'll confirm on WhatsApp.</p>
      </form>
    </section>
  );
}